import { Link, useNavigate } from "react-router-dom";
import { useState, useCallback, useEffect } from "react";
import {
  useReceipts,
  usePendingReceipts,
  useCuentasFiltro,
} from "../hooks/useReceipt";
import { downloadPDF } from "../services/receiptService";
import apiFetch from "../../../../shared/services/apiClient";

import TableHeader from "../components/TableHeader";
import TableBody from "../components/TableBody";
import TableFooter from "../components/TableFooter";
import ImportExportBar from "../../shared/ImportExportBar";
import PendingReceiptsTable from "../../shared/PendingReceiptsTable";

import styles from "../styles/receipt.module.css";
import SendReceiptEmailModal from "../components/SendReceiptEmailModal";
import {
  DeleteReceiptModal,
  EditReceiptModal,
} from "../components/ReceiptActionsModal";

const METODO_LABEL = {
  TRANSFERENCIA: "Transferencia",
  EFECTIVO: "Efectivo",
  CHEQUE: "Cheque",
};

const INITIAL_PARAMS = {
  search: "",
  fecha_desde: "",
  fecha_hasta: "",
  monto_min: "",
  monto_max: "",
  metodo_pago: [],
  cuenta_bancaria: [],
  ordering: "",
};

export default function ListReceipt() {
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [params, setParams] = useState(INITIAL_PARAMS);

  const [importResult, setImportResult] = useState(null);
  const [importPending, setImportPending] = useState([]);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [emailModal, setEmailModal] = useState(null);
  const [deletingReceipt, setDeletingReceipt] = useState(null);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);
  const [selectedMap, setSelectedMap] = useState(new Map());
  const [selectingAll, setSelectingAll] = useState(false);
  const selectedIds = new Set(selectedMap.keys());

  function handleParamsChange(newParams) {
    setParams(newParams);
    setPage(1);
    setSelectedMap(new Map());
  }

  function toggleSelect(id, receiptObj) {
    setSelectedMap((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, receiptObj);
      return next;
    });
  }

  async function selectAll() {
    setSelectingAll(true);
    try {
      const res = await apiFetch("/api/finance/recibo/?page_size=9999");
      const data = await res.json();
      const all = data.results ?? [];
      const map = new Map();
      all.forEach((r) => map.set(r.id, r));
      setSelectedMap(map);
    } catch (_) {
    } finally {
      setSelectingAll(false);
    }
  }

  function clearSelection() {
    setSelectedMap(new Map());
  }

  const { receipts, count, next, previous, loading } = useReceipts(
    page,
    params,
    refreshKey,
  );
  const { pending } = usePendingReceipts(refreshKey);
  const cuentas = useCuentasFiltro();
  const totalPages = Math.ceil(count / 8);

  const handleImportSuccess = useCallback(() => {
    setPage(1);
    setRefreshKey((k) => k + 1);
  }, []);

  function handleResult(resultado, pendientesNuevos) {
    setImportResult(resultado);
    setImportPending(pendientesNuevos ?? []);
  }

  function dismissResult() {
    setImportResult(null);
    setImportPending([]);
  }

  return (
    <>
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>Inicio /</span>
        </Link>
        <Link to="/finanzas/cxc">
          <span className={styles.inactive}> Cuentas por Cobrar /</span>
        </Link>
        <span className={styles.active}> Recibos</span>
      </span>


      {/* ── Fila header ────────────────────────────────────────────── */}
      <div
        className="d-flex justify-content-between align-items-start flex-wrap gap-2"
        style={{ marginBottom: "6px" }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h2 className="mt-2 me-2 p-0 title">Gestión de Recibos</h2>
            {pending.length > 0 && (
              <span
                style={{
                  background: "rgba(249,115,22,0.15)",
                  border: "1px solid rgba(249,115,22,0.35)",
                  color: "#f97316",
                  borderRadius: 20,
                  padding: "2px 10px",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                }}
              >
                <i
                  className="fa-solid fa-triangle-exclamation"
                  style={{ marginRight: 5 }}
                />
                {pending.length} sin aplicar
              </span>
            )}
          </div>

          {actionMsg && (
            <div
              style={{
                background: "rgba(74,222,128,0.08)",
                border: "1px solid rgba(74,222,128,0.3)",
                borderLeft: "4px solid #4ade80",
                borderRadius: 8,
                padding: "10px 16px",
                marginBottom: 12,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                color: "#4ade80",
                fontSize: "0.88rem",
                fontWeight: 500,
              }}
            >
              <span>
                <i
                  className="fa-solid fa-circle-check"
                  style={{ marginRight: 8 }}
                />
                {actionMsg.text}
              </span>
              <button
                onClick={() => setActionMsg(null)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#4ade80",
                  cursor: "pointer",
                  fontSize: "1rem",
                }}
              >
                ✕
              </button>
            </div>
          )}
          <p className="mb-3" style={{color:"#9ca3af"}}>Administra los residentes de tu condominio</p>
        </div>

        <div className="d-flex flex-column gap-2">
          <div className="d-flex gap-2 align-items-center flex-wrap justify-content-end">
            <ImportExportBar
              plantillaUrl="/api/finance/recibo/plantilla-excel/"
              importarUrl="/api/finance/recibo/importar-excel/"
              exportarUrl="/api/finance/recibo/exportar-excel/"
              plantillaFilename="plantilla_recibos.xlsx"
              exportarFilename="recibos.xlsx"
              onImportSuccess={handleImportSuccess}
              onResult={handleResult}
              isReceipts={true}
            />
            <Link
              to="/finanzas/recibos/agregar"
              className={`btn ${styles["btn-add-receipt"]} d-flex gap-2 align-items-center`}
            >
              <i className="fa-solid fa-plus" /> Agregar
            </Link>
          </div>
          <div className="d-flex gap-2 align-items-center justify-content-end flex-wrap">
            {selectedIds.size > 0 && (
              <>
                <span style={{ color: "#9ca3af", fontSize: "0.82rem" }}>
                  {selectedIds.size} seleccionado(s)
                </span>

                <button
                  className="btn"
                  onClick={clearSelection}
                  style={{
                    background: "transparent",
                    color: "#9ca3af",
                    border: "1px solid #374151",
                    fontSize: "0.8rem",
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = "#ef4444";
                    e.currentTarget.style.color = "#ef4444";
                    e.currentTarget.style.background = "rgba(239,68,68,0.1)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = "#374151";
                    e.currentTarget.style.color = "#9ca3af";
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <i className="fa-solid fa-xmark" style={{ marginRight: 4 }} />{" "}
                  Deseleccionar todas
                </button>
                <button
                  className="btn d-flex gap-2 align-items-center"
                  onClick={() =>
                    setEmailModal(Array.from(selectedMap.values()))
                  }
                  style={{
                    background: "#f97316",
                    color: "#fff",
                    border: "1px solid #f97316",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.backgroundColor = "#ea580c")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f97316")
                  }
                >
                  <i className="fa-solid fa-envelope" /> Enviar correos (
                  {selectedIds.size})
                </button>
              </>
            )}
            {selectedIds.size !== count && (
              <button
                className="btn d-flex gap-2 align-items-center"
                onClick={selectAll}
                disabled={selectingAll || count === 0}
                style={{
                  background: "rgba(55,65,81,0.4)",
                  color: "#9ca3af",
                  border: "none",
                  fontSize: "0.8rem",
                }}
                onMouseOver={(e) => (e.currentTarget.style.color = "#f9fafb")}
                onMouseOut={(e) => (e.currentTarget.style.color = "#9ca3af")}
              >
                {selectingAll ? (
                  <>
                    <i className="fa-solid fa-circle-notch fa-spin" />{" "}
                    Seleccionando…
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-check-double" /> Seleccionar todas
                    ({count})
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Modal Estado de Cuenta PDF ─────────────────────────────── */}
      {showPdfModal && <PdfModal onClose={() => setShowPdfModal(false)} />}

      {emailModal && (
        <SendReceiptEmailModal
          recibos={emailModal}
          onClose={() => setEmailModal(null)}
          onSuccess={() => {
            setActionMsg({ text: "Correo(s) enviado(s) correctamente." });
            clearSelection();
          }}
        />
      )}
      {deletingReceipt && (
        <DeleteReceiptModal
          receipt={deletingReceipt}
          onClose={() => setDeletingReceipt(null)}
          onSuccess={(msg) => {
            setActionMsg({ text: msg });
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
      {editingReceipt && (
        <EditReceiptModal
          receipt={editingReceipt}
          onClose={() => setEditingReceipt(null)}
          onSuccess={(msg) => {
            setActionMsg({ text: msg });
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      {/* ── Resultado de importación ───────────────────────────────── */}
      {importResult && (
        <ImportResultBanner
          result={importResult}
          pending={importPending}
          onDismiss={dismissResult}
          onNavigate={(id) => navigate(`/finanzas/recibos/${id}/aplicar`)}
        />
      )}

      {/* ── Pendientes ─────────────────────────────────────────────── */}
      <PendingReceiptsTable
        pendientes={pending}
        pageSize={5}
        title={
          pending.length > 0
            ? `${pending.length} recibo(s) sin facturas aplicadas`
            : undefined
        }
      />

      {/* ── Tabla principal ────────────────────────────────────────── */}
      <div className={styles["table-container"]}>
        <TableHeader
          params={params}
          onParamsChange={handleParamsChange}
          cuentas={cuentas}
          loading={loading}
        />
        <TableBody
          receipts={receipts}
          onEdit={(r) => setEditingReceipt(r)}
          onDelete={(r) => setDeletingReceipt(r)}
          onEmail={(r) => setEmailModal([r])}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
        />
        <TableFooter
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          next={next}
          previous={previous}
          count={count}
          loading={loading}
        />
      </div>
    </>
  );
}

// ── Modal Estado de Cuenta PDF ────────────────────────────────────────────
function PdfModal({ onClose }) {
  const [filiales, setFiliales] = useState([]);
  const [filial, setFilial] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFinal, setFechaFinal] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Cargar filiales reales del condominio
  useEffect(() => {
    apiFetch("/api/residents/filial/")
      .then((r) => r.json())
      .then((data) => {
        const results = data.results ?? data;
        setFiliales(results);
        if (results.length > 0) setFilial(String(results[0].id));
      })
      .catch(() => {});
  }, []);

  async function handleGenerate() {
    if (!filial || !fechaInicio || !fechaFinal) {
      setError("Todos los campos son obligatorios.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const blob = await downloadPDF({
        filial,
        fecha_inicio: fechaInicio,
        fecha_final: fechaFinal,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const filialLabel =
        filiales.find((f) => String(f.id) === filial)?.idFilial ?? filial;
      a.download = `estado_cuenta_${filialLabel}_${fechaInicio.replace(/\//g, "-")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      onClose();
    } catch {
      setError(
        "Error al generar el PDF. Verifique los datos e intente de nuevo.",
      );
    } finally {
      setLoading(false);
    }
  }

  const fieldLabel = {
    display: "block",
    color: "#64748b",
    fontSize: "0.72rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: 5,
  };

  const inputStyle = {
    width: "100%",
    padding: "8px 10px",
    background: "#253040",
    border: "1px solid #344155",
    borderRadius: 6,
    color: "#e2e8f0",
    fontSize: "0.85rem",
    colorScheme: "dark",
    outline: "none",
    transition: "border-color 0.15s",
  };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.65)",
          zIndex: 1000,
          backdropFilter: "blur(3px)",
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          background: "#1a2535",
          border: "1px solid #2d3b4e",
          borderRadius: 12,
          padding: "24px 24px 20px",
          width: "min(440px, 93vw)",
          zIndex: 1001,
          boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
          animation: "fadeIn 0.18s ease-out",
        }}
      >
        {/* Cabecera */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 22,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                background: "rgba(249,115,22,0.15)",
                border: "1px solid rgba(249,115,22,0.25)",
                borderRadius: 9,
                padding: "9px 11px",
                color: "#f97316",
              }}
            >
              <i
                className="fa-solid fa-file-pdf"
                style={{ fontSize: "1.15rem" }}
              />
            </div>
            <div>
              <h3
                style={{
                  margin: 0,
                  color: "#f1f5f9",
                  fontSize: "1.02rem",
                  fontWeight: 700,
                }}
              >
                Estado de Cuenta
              </h3>
              <p
                style={{
                  margin: "2px 0 0",
                  color: "#64748b",
                  fontSize: "0.78rem",
                }}
              >
                Genera el estado financiero por filial en PDF
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#4b5e75",
              cursor: "pointer",
              fontSize: "1.15rem",
              lineHeight: 1,
              padding: "2px 4px",
              marginTop: 2,
              transition: "color 0.15s",
            }}
            onMouseOver={(e) => (e.currentTarget.style.color = "#9ca3af")}
            onMouseOut={(e) => (e.currentTarget.style.color = "#4b5e75")}
          >
            ✕
          </button>
        </div>

        {/* Separador */}
        <div style={{ borderTop: "1px solid #2d3b4e", marginBottom: 20 }} />

        {/* Campos */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Selector de Filial */}
          <div>
            <label style={fieldLabel}>Filial</label>
            <select
              value={filial}
              onChange={(e) => setFilial(e.target.value)}
              style={{ ...inputStyle, cursor: "pointer", colorScheme: "dark" }}
            >
              {filiales.length === 0 && (
                <option value="">Cargando filiales...</option>
              )}
              {filiales.map((f) => (
                <option
                  key={f.id}
                  value={String(f.id)}
                  style={{ background: "#1a2535" }}
                >
                  {f.idFilial}
                  {f.propietarios?.length > 0
                    ? ` — ${f.propietarios[0].nombre ?? ""}`
                    : ""}
                </option>
              ))}
            </select>
          </div>

          {/* Fechas en grid */}
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <div>
              <label style={fieldLabel}>Fecha inicio</label>
              <input
                type="text"
                placeholder="DD/MM/YYYY"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "#f97316")}
                onBlur={(e) => (e.target.style.borderColor = "#344155")}
              />
            </div>
            <div>
              <label style={fieldLabel}>Fecha final</label>
              <input
                type="text"
                placeholder="DD/MM/YYYY"
                value={fechaFinal}
                onChange={(e) => setFechaFinal(e.target.value)}
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "#f97316")}
                onBlur={(e) => (e.target.style.borderColor = "#344155")}
              />
            </div>
          </div>

          {/* Nota de formato */}
          <p style={{ margin: 0, color: "#4b5e75", fontSize: "0.75rem" }}>
            <i className="fa-solid fa-circle-info" style={{ marginRight: 5 }} />
            Las fechas deben tener el formato DD/MM/YYYY (ej: 01/01/2025)
          </p>

          {/* Error */}
          {error && (
            <div
              style={{
                background: "rgba(248,113,113,0.1)",
                border: "1px solid rgba(248,113,113,0.3)",
                borderLeft: "3px solid #f87171",
                borderRadius: 6,
                padding: "8px 12px",
                color: "#fca5a5",
                fontSize: "0.82rem",
              }}
            >
              <i
                className="fa-solid fa-triangle-exclamation"
                style={{ marginRight: 5 }}
              />
              {error}
            </div>
          )}
        </div>

        {/* Separador */}
        <div
          style={{ borderTop: "1px solid #2d3b4e", margin: "20px 0 16px" }}
        />

        {/* Acciones */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              padding: "8px 16px",
              background: "transparent",
              border: "1px solid #2d3b4e",
              borderRadius: 7,
              color: "#64748b",
              fontSize: "0.84rem",
              cursor: "pointer",
              transition: "border-color 0.15s, color 0.15s",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = "#4b5e75";
              e.currentTarget.style.color = "#9ca3af";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = "#2d3b4e";
              e.currentTarget.style.color = "#64748b";
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleGenerate}
            disabled={loading || filiales.length === 0}
            style={{
              padding: "8px 20px",
              background: loading ? "rgba(249,115,22,0.6)" : "#f97316",
              color: "#fff",
              border: "none",
              borderRadius: 7,
              fontSize: "0.84rem",
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 7,
              transition: "background 0.2s",
            }}
            onMouseOver={(e) => {
              if (!loading) e.currentTarget.style.background = "#ea6b0c";
            }}
            onMouseOut={(e) => {
              if (!loading) e.currentTarget.style.background = "#f97316";
            }}
          >
            {loading ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin" /> Generando...
              </>
            ) : (
              <>
                <i className="fa-solid fa-download" /> Descargar PDF
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Banner resultado de importación ──────────────────────────────────────
function ImportResultBanner({ result, pending, onDismiss, onNavigate }) {
  return (
    <div
      style={{
        width: "100%",
        borderRadius: "8px",
        marginBottom: "14px",
        background: result.ok
          ? "rgba(74,222,128,0.07)"
          : "rgba(248,113,113,0.07)",
        border: `1px solid ${result.ok ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)"}`,
        borderLeft: `4px solid ${result.ok ? "#4ade80" : "#f87171"}`,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 16px",
          borderBottom:
            pending.length > 0 || result.errores?.length > 0
              ? "1px solid rgba(55,65,81,0.4)"
              : "none",
        }}
      >
        <span
          style={{
            fontWeight: 600,
            fontSize: "0.88rem",
            color: result.ok ? "#bbf7d0" : "#fecaca",
          }}
        >
          {result.msg}
        </span>
        <button
          onClick={onDismiss}
          style={{
            background: "none",
            border: "none",
            color: "#6b7280",
            cursor: "pointer",
            fontSize: "1rem",
            lineHeight: 1,
            padding: "0 4px",
          }}
        >
          ✕
        </button>
      </div>

      {result.errores?.length > 0 && (
        <div
          style={{
            padding: "10px 16px",
            borderBottom:
              pending.length > 0 ? "1px solid rgba(55,65,81,0.4)" : "none",
          }}
        >
          <details>
            <summary
              style={{
                cursor: "pointer",
                color: "#fb923c",
                fontWeight: 600,
                fontSize: "0.82rem",
              }}
            >
              {result.errores.length} fila(s) con errores
            </summary>
            <ul
              style={{
                marginTop: 8,
                paddingLeft: 18,
                color: "#fca5a5",
                fontSize: "0.8rem",
              }}
            >
              {result.errores.map((e, i) => (
                <li key={i}>
                  <strong>Fila {e.fila}:</strong> {e.errores?.join(", ") ?? e}
                </li>
              ))}
            </ul>
          </details>
        </div>
      )}

      {pending.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <div
            style={{
              padding: "10px 16px 6px",
              color: "#fdba74",
              fontWeight: 600,
              fontSize: "0.82rem",
            }}
          >
            <i
              className="fa-solid fa-triangle-exclamation"
              style={{ marginRight: 6 }}
            />
            {pending.length} recibo(s) importados sin factura — aplícalos:
          </div>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "0.84rem",
            }}
          >
            <thead>
              <tr
                style={{ background: "rgba(55,65,81,0.3)", color: "#9ca3af" }}
              >
                {[
                  "N° Recibo",
                  "Filial",
                  "Monto",
                  "Fecha",
                  "Documento/Ref.",
                  "Acción",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: "8px 16px",
                      textAlign: "left",
                      fontWeight: 600,
                      fontSize: "0.78rem",
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pending.map((p, idx) => (
                <tr
                  key={p.id}
                  style={{
                    borderBottom: "1px solid rgba(55,65,81,0.3)",
                    background:
                      idx % 2 === 0 ? "transparent" : "rgba(55,65,81,0.1)",
                  }}
                >
                  <td
                    style={{
                      padding: "9px 16px",
                      color: "#f9fafb",
                      fontWeight: 600,
                    }}
                  >
                    {p.numero_recibo}
                  </td>
                  <td style={{ padding: "9px 16px", color: "#9ca3af" }}>
                    {p.filial_label}
                  </td>
                  <td>
                    <div style={{ padding:"9px 16px", textAlign:"right",
                      fontFamily:"monospace", fontWeight:600, color:"#9ca3af" }}>
                      ₡{Number(p.monto).toLocaleString("es-ES", { minimumFractionDigits:2, maximumFractionDigits:2 })}
                    </div>
                  </td>
                  <td style={{ padding: "9px 16px", color: "#9ca3af" }}>
                    {p.fecha_pago}
                  </td>
                  <td style={{ padding: "9px 16px", color: "#9ca3af",
                    maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.documento || <span style={{color:"#4b5563"}}>—</span>}
                  </td>
                  <td style={{ padding: "9px 16px" }}>
                    <button
                      onClick={() => onNavigate(p.id)}
                      style={{
                        padding: "4px 14px",
                        background: "#f97316",
                        color: "#fff",
                        border: "none",
                        borderRadius: 5,
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        transition: "background 0.2s",
                        whiteSpace: "nowrap",
                      }}
                      onMouseOver={(e) =>
                        (e.currentTarget.style.background = "#c2570c")
                      }
                      onMouseOut={(e) =>
                        (e.currentTarget.style.background = "#f97316")
                      }
                    >
                      Aplicar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div
            style={{
              padding: "6px 16px",
              color: "#6b7280",
              fontSize: "0.76rem",
              textAlign: "right",
            }}
          >
            Estos recibos también aparecen en la tabla de pendientes de abajo.
          </div>
        </div>
      )}
    </div>
  );
}
